import {ReactNode} from 'react';

import {SelectionRange, TABLE_ROW, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {computeIsAxisGhostCell} from 'notebook/tables/store/logic/TableDimensionsLogic';
import {
  computeIsAxisDimSelected,
  computeSelectionRange,
} from 'notebook/tables/store/logic/TableSelectionLogic';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {
  createDeepSelector,
  createTableStoreSelector,
} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

/**
 * Which cell of which axis a header cell labels. This much is derived identically on both axes, so
 * both axes' models share {@link createAxisHeaderIdentitySelector} for it.
 */
export interface AxisHeaderIdentity {
  /** The axis this header cell labels. */
  dim: TableDimension;
  /** The row index when `dim` is the row axis, otherwise the column index. */
  index: number;
  /** Whether this is a placeholder cell rendered outside of the table's data bounds. */
  isGhostCell: boolean;
  /**
   * How many cells this cell's axis has: the table's row count on the row axis, its column count on
   * the column axis. The cell's context menu compares this against the table's maximum to decide
   * whether inserting the selected number of rows/columns would exceed it.
   */
  numDims: number;
}

/**
 * What every axis (header) cell has, whichever axis it labels: the base that `ColumnHeaderModel`
 * and `RowHeaderModel` extend. The two axes derive these differently — a row label comes from
 * `table.rowLabels`, a column label from its column constraint — so this is a shared shape rather
 * than a shared derivation.
 *
 * Properties belong here only while both axes genuinely have them. As the axes diverge, move
 * properties down into the per-axis model instead of widening this with values that are constant
 * on one axis.
 */
export interface AxisHeaderModel extends AxisHeaderIdentity {
  /**
   * The label rendered in the cell. Null when the axis cell has no label, in which case the cell
   * falls back to the excess label ("A" / "1").
   */
  customLabel: ReactNode;
  /** Whether the cell renders with header styling. */
  isHeaderCell: boolean;
  /**
   * The axis selection range this cell falls within, or null when the cell's index is outside the
   * current selection on its axis. The dimension actions in the cell's context menu operate on this
   * range, and a cell with no range offers no context menu at all.
   */
  selectionRange: SelectionRange | null;
  /**
   * Whether the current selection covers this cell's row (row axis) or column (column axis). Unlike
   * {@link selectionRange} this does not require the whole row/column to be selected, so a plain
   * cell selection still marks the headers it sits under as selected.
   */
  isSelected: boolean;
  /**
   * Whether the selection covers the whole table, in which case the cell's context menu offers
   * "Delete table" in place of deleting the selected rows/columns.
   */
  isTableSelected: boolean;
}

/**
 * Creates a selector for the {@link AxisHeaderIdentity} of the axis cell at (`dim`, `index`).
 * Shared by both axes' model selectors.
 */
export function createAxisHeaderIdentitySelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  dim: TableDimension,
  index: number
): (store: TableEditableStore) => AxisHeaderIdentity {
  return createTableStoreSelector(
    {
      rowCount: (store) => tableSelector(store).dimensions.rowCount,
      columnCount: (store) => tableSelector(store).dimensions.columnCount,
    },
    ({rowCount, columnCount}): AxisHeaderIdentity => ({
      dim,
      index,
      isGhostCell: computeIsAxisGhostCell(dim, index, columnCount),
      numDims: dim === TABLE_ROW ? rowCount : columnCount,
    })
  );
}

/**
 * Creates a selector for the {@link AxisHeaderModel.isSelected} of the axis cell at (`dim`,
 * `index`), from the merge-expanded selection box.
 *
 * The row axis additionally requires an active cell: the row header used to take its selected state
 * from `RowModel`'s per-row selection, which is only derived while the selection has an active cell.
 */
export function createAxisIsSelectedSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  dim: TableDimension,
  index: number
): (store: TableEditableStore) => boolean {
  return (store) => {
    const tableModel = tableSelector(store);
    if (!tableModel.selection.hasMultiSelection) {
      return false;
    }
    if (dim === TABLE_ROW && tableModel.state.multiSelIndex == null) {
      return false;
    }
    return computeIsAxisDimSelected(dim, index, tableModel.selection.expandedBox);
  };
}

/**
 * Creates a selector for the {@link AxisHeaderModel.isTableSelected} of an axis cell on `dim`. Each
 * axis reads it from its own end of the selection: a full row selection already spans every column
 * (and vice versa), so covering every cell of one axis means the whole table is selected.
 */
export function createAxisIsTableSelectedSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  dim: TableDimension
): (store: TableEditableStore) => boolean {
  return (store) => {
    const {selection} = tableSelector(store);
    return dim === TABLE_ROW ? selection.isEveryRowSelected : selection.isEveryColumnSelected;
  };
}

/**
 * Creates a selector for the {@link AxisHeaderModel.selectionRange} of the axis cell at
 * (`dim`, `index`). Shared by both axes' model selectors so that neither axis derives its selection
 * range differently from the other.
 *
 * The range is built fresh on every run, so this is a deep-compared selector rather than a
 * {@link createTableStoreSelector} one: keeping the result's identity stable across equal ranges is
 * what stops a selection change elsewhere in the table from invalidating every axis cell's model.
 */
export function createAxisSelectionRangeSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  dim: TableDimension,
  index: number
): (store: TableEditableStore) => SelectionRange | null {
  return createDeepSelector.withTypes<TableEditableStore>()(
    [(store) => tableSelector(store).selection.multiAxisDimSelection],
    (multiAxisDimSelection): SelectionRange | null => computeSelectionRange(multiAxisDimSelection, dim, index)
  );
}
